import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { STATS } from "@/data/site";

const ABOUT_IMG = "/saivu-office.png";

const VALUES = [
  {
    title: "Fixed & transparent fees",
    desc: "Every proposal states fees, timelines and deliverables in writing — before you commit.",
  },
  {
    title: "Delivery that respects deadlines",
    desc: "A dedicated point of contact, real-time updates, and dates we hold ourselves to.",
  },
  {
    title: "Confidentiality by professional code",
    desc: "Your records live on encrypted cloud drives, seen only by the team on your engagement.",
  },
  {
    title: "Advice before sales",
    desc: "We explain the options, including the ones that don't earn us anything.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About SAIVU — Saivu Consulting Private Limited | Multidisciplinary Consulting Team"
        description="SAIVU Consulting Private Limited is a professional consulting company — a multidisciplinary team of Chartered Accountants, MBA Finance professionals, legal professionals, advocates, tax specialists and business consultants supporting Indian businesses."
        path="/about"
      />

      <section className="bg-mist pt-28 pb-14 sm:pt-32" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-gold-deep sm:text-sm">About SAIVU</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.15] tracking-tight text-navy-900 sm:text-5xl">
              A consulting company built on knowledge, strategy &amp; progress.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink sm:text-lg">
              SAIVU Consulting Private Limited is a professional consulting company focused
              on helping businesses and individuals navigate financial, taxation,
              compliance, legal-support and business requirements.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <img
              src={ABOUT_IMG}
              alt="SAIVU Consulting office — senior consultant reviewing documents while the professional team works in the background, SAIVU branding on the wall"
              className="aspect-[4/3] w-full rounded-lg border border-line object-cover object-center shadow-card md:aspect-[16/10]"
              loading="lazy"
            />
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="text-2xl font-semibold tracking-tight text-navy-900 sm:text-3xl">
                One company, many disciplines.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 flex flex-col gap-5 text-[15px] leading-relaxed text-ink sm:text-base">
                <p>
                  Our multidisciplinary team brings together professionals including
                  Chartered Accountants, MBA Finance professionals, legal professionals,
                  advocates, tax specialists and business consultants — enabling us to
                  provide practical and integrated support across different stages of a
                  business journey.
                </p>
                <p>
                  The practice is led by a senior chartered accountant who brings more than
                  fifteen years of MNC and international audit experience. That grounding
                  shows up in how we work: documents verified before they're filed,
                  calendars planned before deadlines arrive, and advice given in plain
                  language.
                </p>
                <p>
                  We've served more than 4,700 customers. Today, 500+ active clients across
                  22 states stay with us year after year — trust earned one filing at a
                  time.
                </p>
                <p>
                  Our approach is built around knowledge, strategy and progress — combining
                  professional expertise with practical solutions tailored to the
                  requirements of each client.
                </p>
              </div>
            </Reveal>
            <Stagger className="mt-10 grid gap-5 sm:grid-cols-2" gap={0.07}>
              {VALUES.map((v) => (
                <StaggerItem key={v.title} className="h-full">
                  <div className="h-full rounded-lg border border-line bg-mist p-6">
                    <h3 className="text-base font-semibold text-navy-900">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink">{v.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section className="bg-navy-950 py-12" data-testid="about-stats-band">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 sm:px-6 md:grid-cols-4">
          {STATS.map((s) => (
            <Reveal key={s.label}>
              <p className="text-3xl font-semibold text-white sm:text-4xl">{s.value}</p>
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.12em] text-navy-100">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <Reveal>
            <div className="flex flex-wrap items-center justify-between gap-8 rounded-lg border border-line p-8 shadow-card sm:p-10">
              <div className="max-w-xl">
                <h2 className="text-2xl font-semibold tracking-tight text-navy-900 sm:text-3xl">
                  Fifteen minutes with a professional can save a year of cleanup.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-ink sm:text-base">
                  Book a free consultation — we'll map out the exact next step for your
                  business.
                </p>
              </div>
              <Link
                to="/contact"
                className="rounded-md bg-navy-900 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
                data-testid="about-cta-button"
              >
                Book free consult
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
